import { FormEvent, useEffect, useState } from 'react'
import { settingsApi } from '@/api/endpoints'
import type { HourRounding, Settings } from '@/api/types'
import { Button, Card, Field, Input, Select } from '@/components/ui'
import { errorMessage } from '@/utils/errors'

const ROUNDING: { value: HourRounding; label: string }[] = [
  { value: 'NONE', label: 'Přesně (na setiny hodiny)' },
  { value: 'QUARTER', label: 'Na čtvrthodiny' },
  { value: 'HALF', label: 'Na půlhodiny' },
  { value: 'HOUR', label: 'Na celé hodiny' },
]

export default function SettingsPage() {
  const [settings, setSettings] = useState<Settings | null>(null)
  // Secret žije jen v tomhle poli — z backendu se nikdy nevrací.
  const [secret, setSecret] = useState('')
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    settingsApi.get().then(setSettings).catch((err) => setError(errorMessage(err, 'Nastavení se nepodařilo načíst.')))
  }, [])

  const patch = (values: Partial<Settings>) => {
    setSettings((prev) => (prev ? { ...prev, ...values } : prev))
    setSaved(false)
  }

  const persist = async (values?: { clearFakturoidCredentials?: boolean }) => {
    if (!settings) return null
    const { fakturoidConfigured: _c, fakturoidSecretSet: _s, ...rest } = settings
    const updated = await settingsApi.update({
      ...rest,
      ...(secret ? { fakturoidClientSecret: secret } : {}),
      ...values,
    })
    setSettings(updated)
    setSecret('')
    return updated
  }

  const save = async (e: FormEvent) => {
    e.preventDefault()
    setSaving(true)
    setError(null)
    setTestResult(null)
    try {
      await persist()
      setSaved(true)
    } catch (err) {
      setError(errorMessage(err, 'Uložení se nepovedlo.'))
    } finally {
      setSaving(false)
    }
  }

  /** Zkouška spojení má smysl až nad uloženými údaji, tak je nejdřív uložíme. */
  const testConnection = async () => {
    setTesting(true)
    setError(null)
    setTestResult(null)
    try {
      await persist()
      setTestResult(await settingsApi.testFakturoid())
    } catch (err) {
      setTestResult({ ok: false, message: errorMessage(err, 'Spojení se nepovedlo.') })
    } finally {
      setTesting(false)
    }
  }

  const clearCredentials = async () => {
    setError(null)
    setTestResult(null)
    try {
      await persist({ clearFakturoidCredentials: true })
    } catch (err) {
      setError(errorMessage(err, 'Smazání se nepovedlo.'))
    }
  }

  if (!settings) {
    return (
      <div className="space-y-4">
        <h1 className="text-lg font-semibold">Nastavení fakturace</h1>
        {error ? <p className="text-sm text-red-600">{error}</p> : <p className="text-sm text-slate-400">Načítání…</p>}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Nastavení fakturace</h1>

      <form onSubmit={save} className="space-y-4">
        <Card>
          <h2 className="mb-3 font-semibold">DPH</h2>
          <label className="mb-3 inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={settings.vatPayer}
              onChange={(e) => patch({ vatPayer: e.target.checked })}
            />
            Jsme plátce DPH
          </label>
          <div className="max-w-40">
            <Field label="Sazba DPH (%)">
              <Input
                type="number"
                min={0}
                max={100}
                step="0.01"
                value={settings.vatRate}
                disabled={!settings.vatPayer}
                onChange={(e) => patch({ vatRate: Number(e.target.value) })}
              />
            </Field>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Sazba se propíše do vyúčtování v okamžiku jeho uzavření — dřívější vyúčtování si drží tu svoji.
          </p>
        </Card>

        <Card>
          <h2 className="mb-3 font-semibold">Vyúčtování a faktury</h2>
          <div className="flex flex-wrap gap-4">
            <div className="min-w-56 flex-1">
              <Field label="Zaokrouhlení hodin">
                <Select
                  value={settings.hourRounding}
                  onChange={(e) => patch({ hourRounding: e.target.value as HourRounding })}
                >
                  {ROUNDING.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
            <div className="w-40">
              <Field label="Splatnost (dní)">
                <Input
                  type="number"
                  min={0}
                  max={365}
                  value={settings.invoiceDueDays}
                  onChange={(e) => patch({ invoiceDueDays: Number(e.target.value) })}
                />
              </Field>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-500">
            Zaokrouhlení jen předvyplní hodiny při zakládání vyúčtování — vždycky je můžeš přepsat ručně.
          </p>
          <div className="mt-4">
            <Field label="Poznámka na faktuře">
              <Input
                value={settings.invoiceNote ?? ''}
                onChange={(e) => patch({ invoiceNote: e.target.value })}
                placeholder="Např. Fakturujeme dle smlouvy ze dne…"
              />
            </Field>
          </div>
        </Card>

        <Card>
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <h2 className="font-semibold">Fakturoid</h2>
            <span
              className={`rounded px-2 py-0.5 text-xs font-medium ${
                settings.fakturoidConfigured ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
              }`}
            >
              {settings.fakturoidConfigured ? 'připraveno k fakturaci' : 'nedokončené nastavení'}
            </span>
          </div>

          <div className="flex flex-wrap gap-4">
            <div className="min-w-56 flex-1">
              <Field label="Účet (slug z adresy app.fakturoid.cz/…)">
                <Input
                  value={settings.fakturoidSlug ?? ''}
                  onChange={(e) => patch({ fakturoidSlug: e.target.value })}
                  placeholder="individualstartup"
                />
              </Field>
            </div>
            <div className="min-w-56 flex-1">
              <Field label="Client ID">
                <Input
                  value={settings.fakturoidClientId ?? ''}
                  onChange={(e) => patch({ fakturoidClientId: e.target.value })}
                  autoComplete="off"
                />
              </Field>
            </div>
          </div>

          <div className="mt-4 max-w-sm">
            <Field label="Client secret">
              <Input
                type="password"
                value={secret}
                onChange={(e) => {
                  setSecret(e.target.value)
                  setSaved(false)
                }}
                autoComplete="new-password"
                placeholder={settings.fakturoidSecretSet ? '•••••••• uložený, měnit netřeba' : 'vlož secret'}
              />
            </Field>
          </div>

          <p className="mt-2 text-xs text-slate-500">
            Údaje najdeš ve Fakturoidu v Nastavení → Uživatelský účet (OAuth 2, client credentials). Secret se
            ukládá zašifrovaný a zpátky se už nikdy nezobrazí — prázdné pole znamená „nech ten uložený“.
          </p>

          <div className="mt-3 flex flex-wrap items-center gap-3">
            <Button type="button" variant="secondary" onClick={testConnection} disabled={testing}>
              {testing ? 'Zkouším…' : 'Vyzkoušet spojení'}
            </Button>
            {settings.fakturoidSecretSet && (
              <Button type="button" variant="ghost" onClick={clearCredentials}>
                Smazat údaje
              </Button>
            )}
            {testResult && (
              <span className={`text-sm ${testResult.ok ? 'text-emerald-700' : 'text-red-600'}`}>
                {testResult.message}
              </span>
            )}
          </div>
        </Card>

        {error && <p className="text-sm text-red-600">{error}</p>}

        <div className="flex items-center justify-end gap-3">
          {saved && <span className="text-sm text-emerald-700">Uloženo.</span>}
          <Button type="submit" disabled={saving}>
            {saving ? 'Ukládám…' : 'Uložit'}
          </Button>
        </div>
      </form>
    </div>
  )
}
